import { VisuallyHidden } from 'react-aria';
import { Message } from '@gonfalon/messages/Message';
import { Link, LinkButton } from '@launchpad-ui/components';
import type { IconProps } from '@launchpad-ui/icons';
import { Icon } from '@launchpad-ui/icons';
import cx from 'clsx';

import styles from './NewTabLink.module.css';

type NewTabLinkProps = {
  href: string;
  text?: string;
  showIcon?: boolean;
  onClick?: () => void;
  className?: string;
  size?: IconProps['size'];
  variant?: 'default' | 'primary' | 'destructive' | 'minimal' | 'picker';
  renderIconFirst?: boolean;
};

export function NewTabLink({
  className,
  href,
  text = 'read the documentation',
  showIcon = true,
  size = 'small',
  variant,
  renderIconFirst = false,
  onClick,
}: NewTabLinkProps) {
  const shouldRenderIconFirst = showIcon && renderIconFirst;
  const shouldRenderIconLast = showIcon && !renderIconFirst;
  const icon = (
    <Icon
      name="link-external"
      data-test-id="new-tab-link-icon"
      className={cx('Icon', styles.icon, {
        [styles.first]: shouldRenderIconFirst,
        [styles.last]: shouldRenderIconLast,
      })}
      size={size}
    />
  );

  const children = (
    <>
      {shouldRenderIconFirst && icon}
      {text}
      <VisuallyHidden elementType="span">
        <Message id="ui.newTabLink.opensInNewTab" />
      </VisuallyHidden>
      {shouldRenderIconLast && icon}
    </>
  );
  const linkProps = {
    href,
    rel: 'noopener noreferrer',
    target: '_blank',
    onPress: onClick,
    className: cx(styles.link, className),
  };

  return variant ? (
    <LinkButton {...linkProps} variant={variant}>
      {children}
    </LinkButton>
  ) : (
    <Link {...linkProps}>{children}</Link>
  );
}
